import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Modal } from '@mui/material';
import { deleteProgramById, fetchProgramList, toggleProgramStatus } from '../../../redux/actions/Actions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const TABS = [
  { key: 'all',      label: 'All' },
  { key: 'active',   label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const tabStyle = (active) => ({
  padding: '7px 22px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 13,
  background: active ? '#2563EB' : '#F1F5F9',
  color: active ? '#fff' : '#475569',
  transition: 'all 0.15s',
});

const Toggle = ({ checked, onChange }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
    <div
      onClick={onChange}
      style={{
        width: 42, height: 22, borderRadius: 11,
        background: checked ? '#16A34A' : '#CBD5E1',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 22 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 600, color: checked ? '#16A34A' : '#DC2626' }}>
      {checked ? 'Active' : 'Inactive'}
    </span>
  </label>
);

const ProgramsList = () => {
  const fetchPrograms = useSelector((state) => state.fetchAllPrograms.programs);
  const { user } = useSelector((state) => state.auth);
  const userId = user?.role_id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => { dispatch(fetchProgramList()); }, [dispatch]);
  useEffect(() => { setData(fetchPrograms); }, [fetchPrograms]);

  const handleDelete = async (id) => {
    try {
      const response = await dispatch(deleteProgramById(id));
      if (response.status === 200) {
        toast.success('Program Deleted Successfully');
        await dispatch(fetchProgramList());
        setData(prev => prev.filter(p => p.program_id !== id));
      } else if (response.status === 400) {
        toast.error('Unable to delete program; it is currently in use.');
      } else {
        toast.error('Unexpected error');
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Unable to delete program; it is currently in use.');
      } else {
        toast.error('Failed to delete program. Please try again.');
      }
    }
  };

  const filteredData = data.filter(p => {
    if (activeTab === 'active')   return !!p.is_active;
    if (activeTab === 'inactive') return !p.is_active;
    return true;
  });

  const counts = {
    all:      data.length,
    active:   data.filter(p => !!p.is_active).length,
    inactive: data.filter(p => !p.is_active).length,
  };

  return (
    <div>
      {(userId === 1 || userId === 3) ? (
        <>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {TABS.map(t => (
              <button key={t.key} style={tabStyle(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
                {t.label}
                <span style={{ marginLeft: 6, background: activeTab === t.key ? 'rgba(255,255,255,0.25)' : '#E2E8F0', color: activeTab === t.key ? '#fff' : '#64748B', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                  {counts[t.key]}
                </span>
              </button>
            ))}
          </div>

          <table className="table table-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Program Name</th>
                <th>Cost</th>
                <th>Program Details</th>
                <th>Status</th>
                <th>More</th>
                {userId === 1 && <th colSpan={2}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={item.program_id}>
                  <th>{index + 1}</th>
                  <td>{item.program_name}</td>
                  <td>{item.cost}</td>
                  <td>{item.program_details}</td>
                  <td>
                    <Toggle
                      checked={!!item.is_active}
                      onChange={() => dispatch(toggleProgramStatus(item.program_id))}
                    />
                  </td>
                  <td><Button onClick={() => { setSelectedProgram(item); setShowModal(true); }}>View</Button></td>
                  {userId === 1 && (
                    <>
                      <td><Button onClick={() => navigate(`/auth/updateProgram/${item.program_id}`)}>Edit</Button></td>
                      <td><Button onClick={() => handleDelete(item.program_id)}>Delete</Button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'fit-content', backgroundColor: 'white', boxShadow: 24, padding: '20px', fontSize: '1rem', borderRadius: 8, minWidth: 320 }}>
          <h2 style={{ textAlign: 'center' }}>Program Details</h2>
          {selectedProgram && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              {[
                ['Program Name', selectedProgram.program_name],
                ['Duration', selectedProgram.duration],
                ['Cost', selectedProgram.cost],
                ['Target Audience', selectedProgram.target_audience],
                ['Program Coverage', selectedProgram.program_coverage],
                ['Career Opportunities', selectedProgram.career_opportunities],
                ['Registration Charge', selectedProgram.registration_charge],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td style={{ padding: '6px 12px 6px 0', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}:</td>
                  <td style={{ padding: '6px 0' }}>{value}</td>
                </tr>
              ))}
            </table>
          )}
          <Button style={{ marginTop: 16, border: '2px solid black', backgroundColor: 'red', color: 'white' }} onClick={() => setShowModal(false)}><strong>Close</strong></Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProgramsList;
